import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Button, Checkbox, Col, Input, notification, Row, Space, Spin, Tag, Typography } from 'antd';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import useFetch from '../../../../../hooks/useFetch';
import { FetchApiInterface } from '../../../../../interfaces/FetchApi.interface';
import {
  AppstoreOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CompressOutlined,
  EditOutlined,
  GlobalOutlined,
  SaveOutlined,
  LeftOutlined,
  MinusCircleOutlined,
  PhoneOutlined,
  PlusCircleOutlined,
  RightOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import CommitmentImages from '../../components/compromisos/CommitmentImages';

const { Text } = Typography;

interface TraduccionSCSPProps {
  item: any;
  documents?: any[];
  loadingDocuments?: boolean;
  onSwitchToCompromiso?: () => void;
  onCompleted?: () => void;
  isFromComp6?: boolean;
}

const TraduccionSCSP: React.FC<TraduccionSCSPProps> = ({
  item, documents = [], loadingDocuments = false, onSwitchToCompromiso, onCompleted, isFromComp6 = false,
}) => {
  const { fetchApi } = useFetch();
  const { t }        = useTranslation();
  const [docObsOn, setDocObsOn]     = useState<Record<string, boolean>>({});
  const [docObsText, setDocObsText] = useState<Record<string, string>>({});
  const [isEditing, setIsEditing]             = useState(false);
  const [editedTexts, setEditedTexts]         = useState<Record<string, string>>({});
  const [scFirstName, setScFirstName]         = useState('');
  const [spFirstName, setSpFirstName]         = useState('');
  const [saving, setSaving]                   = useState(false);
  const [zoom, setZoom]                       = useState(1);

  useEffect(() => {
    const docWithNames = documents.find((doc: any) => doc?.documentScName || doc?.documentSpName);
    setScFirstName(docWithNames?.documentScName || item?.ascScName || '');
    setSpFirstName(docWithNames?.documentSpName || item?.assSpName || '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, documents]);

  useEffect(() => {
    if (!documents.length) return;
    const obsOn: Record<string, boolean> = {};
    const obsText: Record<string, string> = {};
    documents.forEach((doc: any) => {
      const obs = item?.documentObservation1;
      if (obs) { obsOn[doc.id] = true; obsText[doc.id] = obs; }
    });
    setDocObsOn(obsOn);
    setDocObsText(obsText);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  const handleProcesado = () => {
    if (!item?.id) return;
    const obsIncomplete = documents.some((doc: any) => docObsOn[doc.id] && !docObsText[doc.id]?.trim());
    if (obsIncomplete) {
      notification.warning({ message: t('translatorObservations'), description: t('enterObservations') });
      return;
    }
    const hasAnyEdit = documents.some((doc: any) =>
      editedTexts[doc.id] !== undefined && editedTexts[doc.id] !== (doc.translatedText ?? '')
    );
    if (!hasAnyEdit) {
      notification.warning({ message: t('mustEditTranslation') });
      return;
    }
    const payload = [{
      requestId: item.id,
      translationDirection: item?.translationDirection,
      documents: documents.map((doc: any) => ({
        id: doc.id,
        translatedText: editedTexts[doc.id] ?? doc.translatedText ?? '',
        observation: (docObsOn[doc.id] && docObsText[doc.id]) ? docObsText[doc.id] : null,
        documentScName: scFirstName,
        documentSpName: spFirstName,
      })),
    }];
    setSaving(true);
    const dataSend: FetchApiInterface = { url: '/translations/documents/sc-sp', method: 'PATCH', data: payload };
    fetchApi(dataSend).then((_res: AxiosResponse<any>) => {
      notification.success({ message: t('processedSuccessfully') });
      setIsEditing(false);
      if (docIndex < totalDocs - 1) {
        handleDocChange(docIndex + 1);
      } else if (isFromComp6) {
        fetchApi({ url: '/translations/requests/send', method: 'PATCH', data: { requestIds: [item.id] } } as FetchApiInterface)
          .finally(() => onCompleted ? onCompleted() : window.close());
      } else if (Object.values(docObsOn).some(Boolean)) {
        notification.warning({
          message: t('compromiso6Required'),
          description: t('compromiso6RequiredDescription'),
          duration: 6,
        });
        onSwitchToCompromiso?.();
      } else {
        onCompleted ? onCompleted() : window.close();
      }
    }).catch((err: any) => {
      notification.error({ message: t('error'), description: err?.detail || t('errorProcessingTranslation') });
    }).finally(() => setSaving(false));
  };
  const [docIndex, setDocIndex]               = useState(0);
  const [panX, setPanX]                       = useState(0);
  const [panY, setPanY]                       = useState(0);
  const [dragging, setDragging]               = useState(false);
  const isDragging = useRef(false);
  const lastPos    = useRef({ x: 0, y: 0 });

  const totalDocs   = documents.length;
  const currentDoc  = documents[docIndex] ?? null;
  const docId       = currentDoc?.id;

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom <= 1) return;
    isDragging.current = true;
    setDragging(true);
    lastPos.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };
    setPanX(p => p + dx);
    setPanY(p => p + dy);
  };

  const stopDrag = () => { isDragging.current = false; setDragging(false); };

  const handleDocChange = (next: number) => {
    setDocIndex(next);
    setPanX(0);
    setPanY(0);
  };

  const dialogue = [
    { key: 'conIncludePhoto',  labelKey: 'includesPhoto' },
    { key: 'conAskQuestions',  labelKey: 'answersQuestions' },
    { key: 'conPersonalLife',  labelKey: 'personalLife' },
    { key: 'conExplainDraw',   labelKey: 'explainsDraw' },
    { key: 'conScFeelings',    labelKey: 'expressedFeelings' },
  ].filter(d => item?.[d.key]);

  const participation = [
    { key: 'conPhoto',           labelKey: 'photo' },
    { key: 'conLetter',          labelKey: 'letter' },
    { key: 'conFingerprint',     labelKey: 'fingerprint' },
    { key: 'conScLessFiveYears', labelKey: 'scLessThan5Years' },
  ].filter(p => item?.[p.key]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 800, gap: 0 }}>

      {/* ── Two-column body ── */}
      <Row gutter={[24, 24]} style={{ flex: 1 }}>

        {/* Left: image viewer */}
        <Col xs={24} lg={12} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Image area */}
          <div style={{
            flex: 1,
            border: '1px solid #e8e8e8',
            borderRadius: 6,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 600,
          }}>
            <Spin spinning={loadingDocuments} style={{ flex: 1, display: 'flex' }}>
              <div
                style={{
                  flex: 1,
                  background: '#f0f0f0',
                  overflow: 'hidden',
                  cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'default',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={stopDrag}
                onMouseLeave={stopDrag}
              >
                <div style={{
                  transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
                  transformOrigin: 'top center',
                  transition: dragging ? 'none' : 'transform .2s',
                  width: '100%',
                  padding: '0 16px',
                }}>
                  <CommitmentImages idControl={item?.assignedId || 0} imageIndex={docIndex} />
                </div>
              </div>
            </Spin>

            {/* Controls */}
            <div style={{
              borderTop: '1px solid #e8e8e8',
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fafafa',
            }}>
              <Space size={16}>
                <SearchOutlined      style={{ fontSize: 18, cursor: 'pointer' }} onClick={() => setZoom(z => Math.min(z + 0.1, 2))} />
                <MinusCircleOutlined style={{ fontSize: 18, cursor: 'pointer' }} onClick={() => setZoom(z => Math.max(z - 0.1, 0.5))} />
                <PlusCircleOutlined  style={{ fontSize: 18, cursor: 'pointer' }} onClick={() => setZoom(z => Math.min(z + 0.1, 2))} />
                <CompressOutlined
                  style={{ fontSize: 18, cursor: 'pointer', color: zoom !== 1 || panX !== 0 || panY !== 0 ? '#1677ff' : '#bbb' }}
                  onClick={() => { setZoom(1); setPanX(0); setPanY(0); }}
                />
                <LeftOutlined
                  style={{ fontSize: 18, cursor: docIndex > 0 ? 'pointer' : 'not-allowed', opacity: docIndex > 0 ? 1 : 0.3 }}
                  onClick={() => docIndex > 0 && handleDocChange(docIndex - 1)}
                />
                <Text style={{ fontSize: 13 }}>{totalDocs > 0 ? `${docIndex + 1}/${totalDocs}` : '-'}</Text>
                <RightOutlined
                  style={{ fontSize: 18, cursor: docIndex < totalDocs - 1 ? 'pointer' : 'not-allowed', opacity: docIndex < totalDocs - 1 ? 1 : 0.3, color: '#1677ff' }}
                  onClick={() => docIndex < totalDocs - 1 && handleDocChange(docIndex + 1)}
                />
              </Space>
            </div>

            {/* Tags */}
            {(dialogue.length > 0 || participation.length > 0 || item?.icoTExt) && (
              <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 12px', fontSize: 11 }}>
                {dialogue.length > 0 && (
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>{t('valuableDialogue')}: </Text>
                    {dialogue.map(d => <Tag key={d.key} color="blue" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>{t(d.labelKey)}</Tag>)}
                  </div>
                )}
                {participation.length > 0 && (
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>{t('participation')}: </Text>
                    {participation.map(p => <Tag key={p.key} color="green" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>{t(p.labelKey)}</Tag>)}
                  </div>
                )}
                {item?.icoTExt && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>{t('inappropriateContent')}: </Text>
                    <Text style={{ fontSize: 11 }}>{t(item.icoTExt)}</Text>
                  </div>
                )}
                {item?.pquText && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>{t('processQuality')}: </Text>
                    <Text style={{ fontSize: 11 }}>{t(item.pquText)}</Text>
                  </div>
                )}
              </div>
            )}
          </div>
        </Col>

        {/* Right: info + notes */}
        <Col xs={24} lg={12} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* SC / SP mini-cards */}
          <Row gutter={[10, 10]}>
            {[
              { prefix: 'SC', num: item?.ascScNumber, firstName: scFirstName, full: item?.ascScName, onChange: setScFirstName },
              { prefix: 'SP', num: item?.assSpNumber, firstName: spFirstName, full: item?.assSpName, onChange: setSpFirstName },
            ].map(card => (
              <Col key={card.prefix} xs={24} sm={12}>
                <div style={{
                  border: '1px solid #e8e8e8',
                  borderRadius: 6,
                  padding: '10px 12px',
                  display: 'flex',
                  gap: 10,
                }}>
                  <UserOutlined style={{ fontSize: 20, color: '#bbb', marginTop: 2 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 11, color: '#888' }}>
                      {card.prefix}# <Text strong style={{ fontSize: 13, color: '#000' }}>{card.num ?? '-'}</Text>
                    </div>
                    <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 4 }}>{t('name')}:</div>
                    <Input
                      size="small"
                      value={card.firstName}
                      onChange={e => card.onChange(e.target.value)}
                      style={{ fontWeight: 600, marginBottom: 2 }}
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>{card.full ?? '-'}</Text>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* Detalles Rápidos */}
          <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '8px 12px' }}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>{t('quickDetails')}</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 0', rowGap: 4 }}>
              <Space size={4} style={{ marginRight: 6 }}>
                <CalendarOutlined style={{ color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: 10 }}>{t('age')}:</Text>
                <Text strong style={{ fontSize: 12 }}>{item?.ascScBornDate ? `${moment().diff(moment(item.ascScBornDate), 'years')} ${t('years')}` : '-'}</Text>
              </Space>
              <Text type="secondary" style={{ marginRight: 6 }}>•</Text>
              <Space size={4} style={{ marginRight: 6 }}>
                <PhoneOutlined style={{ color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: 10 }}>{t('communicationRef')}:</Text>
                <Text strong style={{ fontSize: 12 }}>{item?.assRef ?? '-'}</Text>
              </Space>
              <Text type="secondary" style={{ marginRight: 6 }}>•</Text>
              <Space size={4} style={{ marginRight: 6 }}>
                <AppstoreOutlined style={{ color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: 10 }}>{t('communicationType')}:</Text>
                <Text strong style={{ fontSize: 12 }}>{item?.assComType ?? '-'}</Text>
              </Space>
              <Space size={4}>
                  <GlobalOutlined style={{ color: '#8c8c8c' }} />
                  <Text type="secondary" style={{ fontSize: 10 }}>{t('languages')}:</Text>
                  <Text strong style={{ fontSize: 12 }}>
                    {item?.sourceLanguage ?? '-'}
                    <ArrowRightOutlined style={{ margin: '0 5px', fontSize: 10 }} />
                    {item?.targetLanguage ?? '-'}
                  </Text>
                </Space>
            </div>
          </div>

          {/* Notas del Traductor */}
          <div style={{ border: `1px solid ${isEditing ? '#1677ff' : '#e8e8e8'}`, borderRadius: 6, padding: '12px 14px', transition: 'border-color .2s' }}>
            <Text strong style={{ fontSize: 13 }}>{t('translatorNotes')}</Text>
            {item?.translatorNotes && (
              <Text type="secondary" italic style={{ fontSize: 12, display: 'block', marginTop: 2, marginBottom: 6 }}>
                {item.translatorNotes}
              </Text>
            )}
            {isEditing ? (
              <Input.TextArea
                autoSize={{ minRows: 6, maxRows: 20 }}
                value={editedTexts[currentDoc?.id] ?? currentDoc?.translatedText ?? ''}
                placeholder={t('noTranslatedText')}
                onChange={e => currentDoc && setEditedTexts(prev => ({ ...prev, [currentDoc.id]: e.target.value }))}
                style={{ marginTop: 6, fontSize: 13, lineHeight: 1.7 }}
              />
            ) : (
              <div style={{ fontSize: 13, color: '#555', lineHeight: 1.7, maxHeight: 600, overflowY: 'auto', whiteSpace: 'pre-wrap', marginTop: 6 }}>
                {(currentDoc && (editedTexts[currentDoc.id] ?? currentDoc.translatedText))
                  ? (editedTexts[currentDoc.id] ?? currentDoc.translatedText)
                  : <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                      {loadingDocuments ? t('loading') : t('noTranslatedText')}
                    </Text>
                }
              </div>
            )}
          </div>

          {/* Observaciones del Traductor */}
          <div style={{ border: '1px solid #e8e8e8', borderRadius: 6, padding: '12px 14px' }}>
            <Checkbox
              checked={docId ? (docObsOn[docId] ?? false) : false}
              onChange={e => { const val = e.target.checked; setDocObsOn(() => Object.fromEntries(documents.map((d: any) => [d.id, val]))); }}
            >
              <Text strong>{t('translatorObservations')}</Text>
            </Checkbox>
            {docId && docObsOn[docId] && (
              <Input.TextArea
                value={docObsText[docId] ?? ''}
                onChange={e => { const val = e.target.value; setDocObsText(() => Object.fromEntries(documents.map((d: any) => [d.id, val]))); }}
                placeholder={t('enterObservations')}
                required
                rows={3}
                maxLength={500}
                showCount
                style={{ marginTop: 10 }}
              />
            )}
          </div>

        </Col>
      </Row>

      {/* ── Action bar ── */}
      <div style={{
        borderTop: '1px solid #e8e8e8',
        paddingTop: 16,
        marginTop: 16,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
      }}>
        <Button onClick={() => window.close()}>{t('closeWindow')}</Button>
        <Button
          icon={isEditing ? <SaveOutlined /> : <EditOutlined />}
          type={isEditing ? 'default' : 'default'}
          onClick={() => setIsEditing(e => !e)}
        >
          {isEditing ? t('saveEdit') : t('editTranslation')}
        </Button>
        <Button type="primary" icon={<CheckCircleOutlined />} loading={saving} onClick={handleProcesado}>{t('processed')}</Button>
      </div>

    </div>
  );
};

export default TraduccionSCSP;
